import ApiService from '@/services/ApiService';
import { IMPORT_ENTITIES } from '@/components/Company/importer/constants';

class ImporterService {
  // Trailing slashes are required: the Django backend has APPEND_SLASH on, so a
  // slashless path 301-redirects (which drops the method/body on POST).
  static async getJobs(page, filters = '') {
    const composedURL = `/api3/importer/jobs/?page=${page}&page_size=10${filters}`;
    return ApiService.get(composedURL);
  }

  static async getJob(id) {
    const composedURL = `/api3/importer/jobs/${id}/`;
    return ApiService.get(composedURL);
  }

  static async retryJob(id) {
    const composedURL = `/api3/importer/jobs/${id}/retry/`;
    return ApiService.post(composedURL);
  }

  static async downloadContactsExport(jobId) {
    // noRedirect: a 403 here means "not a company admin", not a dead session,
    // so it must surface as an error instead of bouncing the user to Django.
    return ApiService.get(
      `/api3/importer/jobs/${jobId}/contacts-export/`,
      undefined,
      true
    );
  }

  static assertValidEntity(entity) {
    if (!IMPORT_ENTITIES.includes(entity)) {
      throw new Error(
        `Invalid entity: '${entity}'. Expected one of: ${IMPORT_ENTITIES.join(
          ', '
        )}`
      );
    }
  }

  static async uploadFiles({
    files = [],
    entity,
    entities,
    origin,
    jobId,
    apiKey,
  }) {
    const entityList = entities?.length ? entities : null;
    if (!entityList && !entity) {
      throw new Error('uploadFiles requires an entity');
    }
    (entityList ?? [entity]).forEach(ImporterService.assertValidEntity);
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (entityList) {
      formData.append('entities', entityList.join(','));
    } else {
      formData.append('entity', entity);
    }
    if (origin) {
      formData.append('origin', origin);
    }
    if (jobId) {
      formData.append('job_id', jobId);
    }
    if (apiKey) {
      formData.append('api_key', apiKey);
    }
    return ApiService.post('/api3/importer/upload/', formData, undefined, true);
  }

  static async validateApiCredentials({ origin, apiKey }) {
    return ApiService.post('/api3/importer/connection-test/', {
      origin,
      api_key: apiKey,
    });
  }

  static async getMapping(jobId, entity) {
    // Mapping generation can fail with a real server error; do not auto-retry.
    return ApiService.get(
      `/api3/importer/jobs/${jobId}/mapping/?entity=${encodeURIComponent(
        entity
      )}`,
      undefined,
      false,
      true
    );
  }

  static async saveMapping(jobId, { entity, mapping }) {
    return ApiService.put(`/api3/importer/jobs/${jobId}/mapping/`, {
      entity,
      mapping,
    });
  }

  static async getValueMappings(jobId) {
    return ApiService.get(`/api3/importer/jobs/${jobId}/value-mappings/`);
  }

  static async saveAgentMappings(jobId, body) {
    return ApiService.put(
      `/api3/importer/jobs/${jobId}/value-mappings/agents/`,
      body
    );
  }

  static async saveLeadStatusMappings(jobId, body) {
    return ApiService.put(
      `/api3/importer/jobs/${jobId}/value-mappings/lead-status/`,
      body
    );
  }

  static async startJob(id, policies = {}) {
    return ApiService.post(`/api3/importer/jobs/${id}/start/`, policies);
  }

  static async deleteJobFile(jobId, fileId) {
    return ApiService.delete(`/api3/importer/jobs/${jobId}/files/${fileId}/`);
  }

  static async getCrmRequiredFiles(origin, instances) {
    return ApiService.post('/api3/importer/crm/required-files/', {
      origin,
      instances,
    });
  }

  static async saveCrmConfig(jobId, instances) {
    return ApiService.post(`/api3/importer/jobs/${jobId}/crm-config/`, {
      instances,
    });
  }

  static async getPortalStatus(origin) {
    return ApiService.get(
      `/api3/importer/portal-status/?origin=${encodeURIComponent(origin)}`
    );
  }
}

export default ImporterService;
